import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api.service';
import { useTenantStore } from '../../stores/tenant.store';
import { useAccountingPeriodStore } from '../../stores/accounting-period.store';
import type { AccountingViewId } from './tabRoutes';
import { ManualJournalForm } from './ManualJournalForm';

type Tab = Exclude<AccountingViewId, 'mappings' | 'fiscalYears' | 'dashboard'>;

export function AccountingPanels({ view }: { view: Tab }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const tab = view;
  const dateFrom = useAccountingPeriodStore((s) => s.dateFrom);
  const dateTo = useAccountingPeriodStore((s) => s.dateTo);
  const setDateFrom = useAccountingPeriodStore((s) => s.setDateFrom);
  const setDateTo = useAccountingPeriodStore((s) => s.setDateTo);
  const [loading, setLoading] = useState(false);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [journal, setJournal] = useState<any>(null);
  const [ledgerAccountId, setLedgerAccountId] = useState('');
  const [ledger, setLedger] = useState<any>(null);
  const [trial, setTrial] = useState<any>(null);
  const [income, setIncome] = useState<any>(null);
  const [balanceSheet, setBalanceSheet] = useState<any>(null);
  const [cashFlow, setCashFlow] = useState<any>(null);
  const [integrity, setIntegrity] = useState<any>(null);
  const [costCenters, setCostCenters] = useState<any[]>([]);
  const [businessUnits, setBusinessUnits] = useState<any[]>([]);
  const [dimFilters, setDimFilters] = useState({
    branchId: '',
    costCenterId: '',
    businessUnitId: '',
  });
  const [includeReversed, setIncludeReversed] = useState(false);
  const [compareFrom, setCompareFrom] = useState('');
  const [compareTo, setCompareTo] = useState('');
  const [branches, setBranches] = useState<any[]>([]);
  const [wpPacks, setWpPacks] = useState<any[]>([]);
  const [fiscalYears, setFiscalYears] = useState<any[]>([]);
  const [wpForm, setWpForm] = useState({ fiscalYearId: '', title: '' });
  const [wpItemForm, setWpItemForm] = useState({
    packId: '',
    grouping: '',
    accountId: '',
    counterAccountId: '',
    adjustmentDebit: '',
  });
  const [ccForm, setCcForm] = useState({ code: '', name: '', description: '' });
  const [buForm, setBuForm] = useState({ code: '', name: '', description: '' });
  const [segmentBy, setSegmentBy] = useState<'branch' | 'costCenter' | 'businessUnit'>('costCenter');
  const [segmented, setSegmented] = useState<any>(null);
  const [inflationSeries, setInflationSeries] = useState<any[]>([]);
  const [inflationValues, setInflationValues] = useState<any[]>([]);
  const [inflationPolicy, setInflationPolicy] = useState<any>(null);
  const [inflationRuns, setInflationRuns] = useState<any[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const [selectedRun, setSelectedRun] = useState<any>(null);
  const [seriesForm, setSeriesForm] = useState({ code: '', name: '', source: '' });
  const [valueForm, setValueForm] = useState({ period: '', value: '' });
  const [simForm, setSimForm] = useState({ fromPeriod: '', toPeriod: '' });
  const [inflationBusy, setInflationBusy] = useState(false);
  const [importJobs, setImportJobs] = useState<any[]>([]);
  const [importJob, setImportJob] = useState<any>(null);
  const [importType, setImportType] = useState<
    'CHART_OF_ACCOUNTS' | 'OPENING_BALANCES' | 'JOURNAL_ENTRIES'
  >('CHART_OF_ACCOUNTS');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importForce, setImportForce] = useState(false);
  const [importEntryDate, setImportEntryDate] = useState('');
  const [importChartMode, setImportChartMode] = useState<'upsert' | 'createOnly'>('upsert');
  const [importBusy, setImportBusy] = useState(false);
  const [fxRates, setFxRates] = useState<any[]>([]);
  const [fxRuns, setFxRuns] = useState<any[]>([]);
  const [fxRun, setFxRun] = useState<any>(null);
  const [fxForm, setFxForm] = useState({
    currency: 'USD',
    date: '',
    rate: '',
    source: 'MANUAL',
  });
  const [fxAsOf, setFxAsOf] = useState('');
  const [fxEnabled, setFxEnabled] = useState(false);
  const [fxBusy, setFxBusy] = useState(false);
  const [showManualJournal, setShowManualJournal] = useState(
    () => searchParams.get('action') === 'new',
  );

  const dimQuery = {
    ...(dimFilters.branchId ? { branchId: dimFilters.branchId } : {}),
    ...(dimFilters.costCenterId ? { costCenterId: dimFilters.costCenterId } : {}),
    ...(dimFilters.businessUnitId ? { businessUnitId: dimFilters.businessUnitId } : {}),
    ...(includeReversed ? { includeReversed: true } : {}),
    ...(compareFrom && compareTo ? { compareFrom, compareTo } : {}),
  };

  const monthRange = useMemo(
    () => ({
      dateFrom,
      dateTo,
      asOf: dateTo,
    }),
    [dateFrom, dateTo],
  );

  useEffect(() => {
    if (!dateFrom || !dateTo) {
      const now = new Date();
      const from = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
      const to = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0));
      if (!dateFrom) setDateFrom(from.toISOString().slice(0, 10));
      if (!dateTo) setDateTo(to.toISOString().slice(0, 10));
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setShowManualJournal(true);
    }
  }, [searchParams]);

  const load = async () => {
    if (!legalEntityId) {
      setError('Seleccioná una empresa en la barra superior');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (tab === 'chart') {
        setAccounts(await apiService.getAccountingChart(legalEntityId));
      } else if (tab === 'periods') {
        setPeriods(await apiService.getAccountingPeriods(legalEntityId));
      } else if (tab === 'journal') {
        const [book, ccs, bus, br, chart] = await Promise.all([
          apiService.getAccountingDailyBook({
            legalEntityId,
            dateFrom: monthRange.dateFrom,
            dateTo: monthRange.dateTo,
            take: 100,
            skip: 0,
            ...dimQuery,
          }),
          apiService.listCostCenters(legalEntityId),
          apiService.listBusinessUnits(legalEntityId),
          apiService.getBranches(true, legalEntityId),
          apiService.getAccountingChart(legalEntityId),
        ]);
        setJournal(book);
        setCostCenters(ccs);
        setBusinessUnits(bus);
        setBranches(br);
        setAccounts(chart);
      } else if (tab === 'workingPapers') {
        const [packs, fys, chart] = await Promise.all([
          apiService.listWorkingPaperPacks(legalEntityId),
          apiService.getFiscalYears(legalEntityId),
          apiService.getAccountingChart(legalEntityId),
        ]);
        setWpPacks(packs);
        setFiscalYears(fys);
        setAccounts(chart);
      } else if (tab === 'imports') {
        setImportJobs(await apiService.listAccountingImports(legalEntityId));
      } else if (tab === 'multicurrency') {
        const [rates, runs] = await Promise.all([
          apiService.listExchangeRates(legalEntityId),
          apiService.listFxRevaluations(legalEntityId),
        ]);
        setFxRates(rates);
        setFxRuns(runs);
        try {
          const pol = await apiService.getFxPolicy(legalEntityId);
          setFxEnabled(!!pol?.fxRevaluationEnabled);
        } catch {
          setFxEnabled(false);
        }
      } else if (tab === 'ledger') {
        const [chart, ccs, bus] = await Promise.all([
          apiService.getAccountingChart(legalEntityId),
          apiService.listCostCenters(legalEntityId),
          apiService.listBusinessUnits(legalEntityId),
        ]);
        setAccounts(chart);
        setCostCenters(ccs);
        setBusinessUnits(bus);
        const posting = chart.find((a: any) => a.isPosting);
        const accountId = ledgerAccountId || posting?.id;
        if (accountId) {
          setLedgerAccountId(accountId);
          setLedger(
            await apiService.getAccountingReportLedger({
              legalEntityId,
              accountId,
              dateFrom: monthRange.dateFrom,
              dateTo: monthRange.dateTo,
              take: 100,
              skip: 0,
              ...dimQuery,
            }),
          );
        }
      } else if (tab === 'trial') {
        const [tb, ccs, bus] = await Promise.all([
          apiService.getTrialBalance({
            legalEntityId,
            dateFrom: monthRange.dateFrom,
            dateTo: monthRange.dateTo,
            ...dimQuery,
          }),
          apiService.listCostCenters(legalEntityId),
          apiService.listBusinessUnits(legalEntityId),
        ]);
        setTrial(tb);
        setCostCenters(ccs);
        setBusinessUnits(bus);
      } else if (tab === 'income') {
        const [is, seg, ccs, bus] = await Promise.all([
          apiService.getIncomeStatement({
            legalEntityId,
            dateFrom: monthRange.dateFrom,
            dateTo: monthRange.dateTo,
            ...dimQuery,
          } as any),
          apiService.getIncomeStatementSegmented({
            legalEntityId,
            dateFrom: monthRange.dateFrom,
            dateTo: monthRange.dateTo,
            segmentBy,
          }),
          apiService.listCostCenters(legalEntityId),
          apiService.listBusinessUnits(legalEntityId),
        ]);
        setIncome(is.current ?? is);
        setSegmented(seg);
        setCostCenters(ccs);
        setBusinessUnits(bus);
      } else if (tab === 'costCenters') {
        setCostCenters(await apiService.listCostCenters(legalEntityId));
      } else if (tab === 'businessUnits') {
        setBusinessUnits(await apiService.listBusinessUnits(legalEntityId, true));
      } else if (tab === 'inflation') {
        const [series, policy, runs, chart] = await Promise.all([
          apiService.listInflationSeries(true),
          apiService.getInflationPolicy(legalEntityId),
          apiService.listInflationRuns(legalEntityId),
          apiService.getAccountingChart(legalEntityId),
        ]);
        setInflationSeries(series);
        setInflationPolicy(policy);
        setInflationRuns(runs);
        setAccounts(chart);
        const sid = selectedSeriesId || policy?.inflationIndexSeriesId || series[0]?.id || '';
        if (sid) {
          setSelectedSeriesId(sid);
          setInflationValues(await apiService.listInflationValues(sid));
        } else {
          setInflationValues([]);
        }
      } else if (tab === 'balance') {
        const bs = await apiService.getBalanceSheet({
          legalEntityId,
          asOf: monthRange.asOf,
          ...dimQuery,
          ...(compareTo ? { compareAsOf: compareTo } : {}),
        } as any);
        setBalanceSheet(bs.current ?? bs);
      } else if (tab === 'cashflow') {
        setCashFlow(
          await apiService.getCashFlowReport({
            legalEntityId,
            dateFrom: monthRange.dateFrom,
            dateTo: monthRange.dateTo,
          }),
        );
      } else if (tab === 'integrity') {
        setIntegrity(await apiService.getAccountingIntegrity(legalEntityId));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntityId, tab, dimFilters.branchId, dimFilters.costCenterId, dimFilters.businessUnitId, segmentBy, includeReversed, dateFrom, dateTo, compareFrom, compareTo]);

  const closePeriod = async (periodId: string) => {
    setClosingId(periodId);
    setError(null);
    try {
      const pre = await apiService.preCloseAccountingPeriod(periodId);
      if (!pre.ready) {
        const codes = (pre.findings ?? [])
          .filter((f: any) => f.blocking && f.severity === 'WARNING')
          .map((f: any) => f.code);
        if (pre.status === 'NOT_READY' && (pre.errorCount ?? 0) > 0) {
          setError(
            `Pre-cierre NOT_READY: ${(pre.findings ?? [])
              .filter((f: any) => f.severity === 'ERROR')
              .map((f: any) => f.message)
              .join('; ')}`,
          );
          return;
        }
        await apiService.closeAccountingPeriod(periodId, {
          acceptWarningCodes: codes,
        });
      } else {
        await apiService.closeAccountingPeriod(periodId);
      }
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message?.message ??
        e?.response?.data?.message ??
        e.message;
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setClosingId(null);
    }
  };

  const reportExportParams = {
    legalEntityId: legalEntityId || '',
    dateFrom: monthRange.dateFrom,
    dateTo: monthRange.dateTo,
    asOf: monthRange.asOf,
    ...dimQuery,
    includeReversed: includeReversed ? 'true' : undefined,
  };

  const dimFilterBar = (
    <div className="flex flex-wrap gap-3 items-end mb-3">
      <div className="text-xs text-gray-500 pb-2">
        Período:{' '}
        <span className="font-medium text-gray-800">
          {monthRange.dateFrom} → {monthRange.dateTo}
        </span>
        <span className="text-gray-400"> (cambiar en el encabezado del módulo)</span>
      </div>
      <label className="text-sm">
        <span className="block text-xs text-gray-500">Comparar desde</span>
        <input
          type="date"
          className="border rounded-md px-2 py-1.5 text-sm"
          value={compareFrom}
          onChange={(e) => setCompareFrom(e.target.value)}
        />
      </label>
      <label className="text-sm">
        <span className="block text-xs text-gray-500">Comparar hasta</span>
        <input
          type="date"
          className="border rounded-md px-2 py-1.5 text-sm"
          value={compareTo}
          onChange={(e) => setCompareTo(e.target.value)}
        />
      </label>
      <label className="text-sm">
        <span className="block text-xs text-gray-500">Sucursal</span>
        <select
          className="border rounded-md px-2 py-1.5 text-sm"
          value={dimFilters.branchId}
          onChange={(e) => setDimFilters((d) => ({ ...d, branchId: e.target.value }))}
        >
          <option value="">Todas</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.code} — {b.name}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-xs text-gray-500">Centro de costo</span>
        <select
          className="border rounded-md px-2 py-1.5 text-sm"
          value={dimFilters.costCenterId}
          onChange={(e) => setDimFilters((d) => ({ ...d, costCenterId: e.target.value }))}
        >
          <option value="">Todos</option>
          {costCenters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-xs text-gray-500">Unidad de negocio</span>
        <select
          className="border rounded-md px-2 py-1.5 text-sm"
          value={dimFilters.businessUnitId}
          onChange={(e) => setDimFilters((d) => ({ ...d, businessUnitId: e.target.value }))}
        >
          <option value="">Todas</option>
          {businessUnits.map((b) => (
            <option key={b.id} value={b.id}>
              {b.code} — {b.name}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm flex items-center gap-2 pb-2">
        <input
          type="checkbox"
          checked={includeReversed}
          onChange={(e) => setIncludeReversed(e.target.checked)}
        />
        Incluir revertidos
      </label>
    </div>
  );

  const createCostCenter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalEntityId) return;
    setLoading(true);
    try {
      await apiService.createCostCenter({ legalEntityId, ...ccForm });
      setCcForm({ code: '', name: '', description: '' });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const createBusinessUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalEntityId) return;
    setLoading(true);
    try {
      await apiService.createBusinessUnit({ legalEntityId, ...buForm });
      setBuForm({ code: '', name: '', description: '' });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const createInflationSeries = async (e: React.FormEvent) => {
    e.preventDefault();
    setInflationBusy(true);
    setError(null);
    try {
      const created = await apiService.createInflationSeries(seriesForm);
      setSeriesForm({ code: '', name: '', source: '' });
      setSelectedSeriesId(created.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setInflationBusy(false);
    }
  };

  const upsertInflationValue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSeriesId) return;
    setInflationBusy(true);
    setError(null);
    try {
      await apiService.upsertInflationValue(selectedSeriesId, {
        period: valueForm.period,
        value: valueForm.value,
      });
      setValueForm({ period: '', value: '' });
      setInflationValues(await apiService.listInflationValues(selectedSeriesId));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setInflationBusy(false);
    }
  };

  const saveInflationPolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalEntityId || !inflationPolicy) return;
    setInflationBusy(true);
    setError(null);
    try {
      setInflationPolicy(
        await apiService.patchInflationPolicy(legalEntityId, {
          inflationAdjustmentEnabled: !!inflationPolicy.inflationAdjustmentEnabled,
          inflationMathEngine: inflationPolicy.inflationMathEngine || 'INDEX_RATIO',
          inflationIndexSeriesId: inflationPolicy.inflationIndexSeriesId || null,
          inflationResultAccountId: inflationPolicy.inflationResultAccountId || null,
        }),
      );
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setInflationBusy(false);
    }
  };

  const runInflationSimulation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalEntityId) return;
    setInflationBusy(true);
    setError(null);
    try {
      const draft = await apiService.createInflationRun({
        legalEntityId,
        fromPeriod: simForm.fromPeriod,
        toPeriod: simForm.toPeriod,
      });
      const calculated = await apiService.calculateInflationRun(draft.id);
      setSelectedRun(calculated);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setInflationBusy(false);
    }
  };

  const postSelectedInflationRun = async () => {
    if (!selectedRun?.id) return;
    setInflationBusy(true);
    setError(null);
    try {
      const posted = await apiService.postInflationRun(selectedRun.id);
      setSelectedRun(posted);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setInflationBusy(false);
    }
  };

  const reverseSelectedInflationRun = async () => {
    if (!selectedRun?.id) return;
    const reason = window.prompt('Motivo de reversión');
    if (!reason?.trim()) return;
    setInflationBusy(true);
    setError(null);
    try {
      const reversed = await apiService.reverseInflationRun(selectedRun.id, reason.trim());
      setSelectedRun(reversed);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err.message);
    } finally {
      setInflationBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={load}
          disabled={loading || !legalEntityId}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          {loading ? 'Cargando…' : 'Actualizar'}
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {tab === 'chart' && legalEntityId && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Código</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Nombre</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Tipo</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Naturaleza</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Imputable</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {accounts.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-2 font-mono text-sm" style={{ paddingLeft: 8 + a.level * 12 }}>
                    {a.code}
                  </td>
                  <td className="px-4 py-2 text-sm">{a.name}</td>
                  <td className="px-4 py-2 text-sm">{a.type}</td>
                  <td className="px-4 py-2 text-sm">{a.nature}</td>
                  <td className="px-4 py-2 text-sm">{a.isPosting ? 'Sí' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'periods' && legalEntityId && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Período</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Estado</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Apertura</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Cierre</th>
                <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {periods.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500 text-sm">
                    Sin períodos aún. Se crean al postear el primer asiento del mes.
                  </td>
                </tr>
              ) : (
                periods.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2 text-sm font-mono">
                      {p.year}-{String(p.month).padStart(2, '0')}
                    </td>
                    <td className="px-4 py-2 text-sm">{p.status}</td>
                    <td className="px-4 py-2 text-sm">
                      {p.openedAt ? new Date(p.openedAt).toLocaleString('es-AR') : '—'}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {p.closedAt ? new Date(p.closedAt).toLocaleString('es-AR') : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {p.status === 'OPEN' && (
                        <button
                          type="button"
                          disabled={closingId === p.id}
                          onClick={() => closePeriod(p.id)}
                          className="text-sm px-3 py-1 rounded bg-emerald-700 text-white disabled:opacity-50"
                        >
                          {closingId === p.id ? 'Cerrando…' : 'Pre-cierre → Cerrar'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'journal' && legalEntityId && (
        <div className="space-y-4">
          {showManualJournal ? (
            <ManualJournalForm
              legalEntityId={legalEntityId}
              defaultDate={monthRange.dateTo || monthRange.dateFrom}
              accounts={accounts}
              onCancel={() => {
                setShowManualJournal(false);
                if (searchParams.get('action') === 'new') {
                  searchParams.delete('action');
                  setSearchParams(searchParams, { replace: true });
                }
              }}
              onDone={async () => {
                setShowManualJournal(false);
                if (searchParams.get('action') === 'new') {
                  searchParams.delete('action');
                  setSearchParams(searchParams, { replace: true });
                }
                await load();
              }}
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="px-3 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800"
                onClick={() => setShowManualJournal(true)}
              >
                + Nuevo asiento manual
              </button>
            </div>
          )}
          {dimFilterBar}
          <div className="flex gap-2">
            <button
              type="button"
              className="text-xs px-3 py-1.5 rounded border"
              onClick={() =>
                apiService.downloadAccountingExport(
                  'daily-book',
                  'xlsx',
                  reportExportParams,
                  'diario.xlsx',
                )
              }
            >
              Exportar XLSX
            </button>
            <button
              type="button"
              className="text-xs px-3 py-1.5 rounded border"
              onClick={() =>
                apiService.downloadAccountingExport(
                  'daily-book',
                  'csv',
                  reportExportParams,
                  'diario.csv',
                )
              }
            >
              Exportar CSV
            </button>
          </div>
          {journal?.totals && (
            <p className="text-sm text-gray-600">
              Totales: Debe {Number(journal.totals.debit).toLocaleString()} · Haber{' '}
              {Number(journal.totals.credit).toLocaleString()} ·{' '}
              {journal.totals.balanced ? 'Balanceado' : 'Desbalanceado'}
            </p>
          )}
          {(journal?.entries ?? []).map((e: any) => (
            <div key={e.id} className="bg-white border rounded-lg p-4">
              <div className="flex flex-wrap justify-between gap-2 text-sm mb-2">
                <span className="font-mono font-semibold">
                  #{e.entryNumber ?? 'DRAFT'} · {e.status}
                </span>
                <span>{new Date(e.entryDate).toLocaleDateString('es-AR', { timeZone: 'UTC' })}</span>
              </div>
              <p className="text-sm text-gray-700 mb-2">{e.description}</p>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1">Cuenta</th>
                    <th className="py-1 text-right">Debe</th>
                    <th className="py-1 text-right">Haber</th>
                  </tr>
                </thead>
                <tbody>
                  {e.lines?.map((l: any) => (
                    <tr key={l.id} className="border-t">
                      <td className="py-1">
                        <span className="font-mono text-gray-500">{l.account?.code}</span> {l.account?.name}
                      </td>
                      <td className="py-1 text-right">
                        {Number(l.debit) ? Number(l.debit).toLocaleString() : ''}
                      </td>
                      <td className="py-1 text-right">
                        {Number(l.credit) ? Number(l.credit).toLocaleString() : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {(journal?.entries?.length ?? 0) === 0 && (
            <p className="text-center text-gray-500 py-8">Sin asientos en el período.</p>
          )}
        </div>
      )}

      {tab === 'ledger' && legalEntityId && (
        <div className="space-y-4">
          {dimFilterBar}
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={ledgerAccountId}
            onChange={async (e) => {
              const accountId = e.target.value;
              setLedgerAccountId(accountId);
              if (!legalEntityId || !accountId) return;
              setLedger(
                await apiService.getAccountingReportLedger({
                  legalEntityId,
                  accountId,
                  dateFrom: monthRange.dateFrom,
                  dateTo: monthRange.dateTo,
                  ...dimQuery,
                }),
              );
            }}
          >
            {accounts
              .filter((a) => a.isPosting)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name}
                </option>
              ))}
          </select>
          {ledger && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="px-4 py-2 text-sm border-b bg-gray-50">
                Saldo apertura: {Number(ledger.openingBalance).toLocaleString()} · Cierre:{' '}
                {Number(ledger.closingBalance).toLocaleString()}
              </div>
              <table className="min-w-full divide-y">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Fecha</th>
                    <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Asiento</th>
                    <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Debe</th>
                    <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Haber</th>
                    <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ledger.rows?.map((r: any, i: number) => (
                    <tr key={i}>
                      <td className="px-4 py-2 text-sm">
                        {new Date(r.date).toLocaleDateString('es-AR')}
                      </td>
                      <td className="px-4 py-2 text-sm font-mono">{r.entryNumber}</td>
                      <td className="px-4 py-2 text-sm text-right">{Number(r.debit).toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm text-right">{Number(r.credit).toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm text-right">{Number(r.balance).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'trial' && legalEntityId && trial && (
        <div className="space-y-3">
        {dimFilterBar}
        <div className="flex gap-2">
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded border"
            onClick={() =>
              apiService.downloadAccountingExport(
                'trial-balance',
                'xlsx',
                reportExportParams,
                'sumas-saldos.xlsx',
              )
            }
          >
            Exportar XLSX
          </button>
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded border"
            onClick={() =>
              apiService.downloadAccountingExport(
                'trial-balance',
                'csv',
                reportExportParams,
                'sumas-saldos.csv',
              )
            }
          >
            Exportar CSV
          </button>
        </div>
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-2 text-xs bg-gray-50 text-gray-600 border-b">
            Debe {Number((trial.current ?? trial).totals?.movementsDebit).toLocaleString()} · Haber{' '}
            {Number((trial.current ?? trial).totals?.movementsCredit).toLocaleString()} ·{' '}
            {(trial.current ?? trial).totals?.balanced ? 'OK' : 'Desbalance'}
            {trial.variance ? ' · Comparativo activo' : ''}
          </div>
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Cuenta</th>
                <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Saldo inicial</th>
                <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Débitos</th>
                <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Créditos</th>
                <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Saldo final</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {((trial.current ?? trial).rows ?? []).map((b: any) => (
                <tr key={b.code}>
                  <td className="px-4 py-2 text-sm">
                    <div className="font-mono text-gray-500">{b.code}</div>
                    <div>{b.name}</div>
                  </td>
                  <td className="px-4 py-2 text-sm text-right">
                    {Number(b.openingBalance ?? 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-right">
                    {Number(b.movementsDebit).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-right">
                    {Number(b.movementsCredit).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-medium">
                    {Number(b.closingBalance ?? b.rolledNetBalance ?? b.netBalance).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {tab === 'income' && legalEntityId && income && (
        <div className="space-y-4">
          {dimFilterBar}
          <div className="flex flex-wrap gap-3 items-end">
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Segmentar por</span>
              <select
                className="border rounded-md px-2 py-1.5 text-sm"
                value={segmentBy}
                onChange={(e) =>
                  setSegmentBy(e.target.value as 'branch' | 'costCenter' | 'businessUnit')
                }
              >
                <option value="branch">Sucursal</option>
                <option value="costCenter">Centro de costo</option>
                <option value="businessUnit">Unidad de negocio</option>
              </select>
            </label>
            {segmented?.reconciliation && (
              <p className="text-xs text-gray-600">
                Reconciliación Total vs ER:{' '}
                {segmented.reconciliation.reconciled ? 'OK' : 'DIFF'} (
                {segmented.reconciliation.difference})
              </p>
            )}
          </div>
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b text-sm flex items-center justify-between gap-3">
            <div>
              Ingresos {Number(income.totalIncome).toLocaleString()} − Egresos{' '}
              {Number(income.totalExpense).toLocaleString()} ={' '}
              <strong>{Number(income.result).toLocaleString()}</strong>
            </div>
            <button
              type="button"
              className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
              onClick={() =>
                apiService.downloadIncomeStatementPdf({
                  legalEntityId,
                  dateFrom: monthRange.dateFrom,
                  dateTo: monthRange.dateTo,
                })
              }
            >
              Descargar PDF
            </button>
          </div>
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Cuenta / Nodo</th>
                <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Importe</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(income.mode === 'STRUCTURE' ? income.nodes : income.rows)?.map((r: any) => (
                <tr key={r.code + (r.id ?? '')}>
                  <td className="px-4 py-2 text-sm">
                    <span className="font-mono text-gray-500">{r.code}</span> {r.name}
                    {r.type ? ` (${r.type})` : ''}
                  </td>
                  <td className="px-4 py-2 text-sm text-right">{Number(r.amount).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

          {segmented && (
            <div className="bg-white rounded-lg border overflow-x-auto">
              <div className="px-4 py-2 text-sm border-b bg-gray-50">
                ER segmentado · {segmented.segmentBy} · Resultado{' '}
                {Number(segmented.totals?.result).toLocaleString()}
              </div>
              <table className="min-w-full divide-y text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Cuenta</th>
                    {(segmented.columns ?? [])
                      .filter((c: any) => c.id !== '__TOTAL__')
                      .map((c: any) => (
                        <th key={c.id} className="px-3 py-2 text-right text-xs uppercase text-gray-500">
                          {c.code}
                        </th>
                      ))}
                    <th className="px-3 py-2 text-right text-xs uppercase text-gray-500">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(segmented.rows ?? []).map((r: any) => (
                    <tr key={r.code}>
                      <td className="px-3 py-2">
                        <span className="font-mono text-gray-500">{r.code}</span> {r.name}
                      </td>
                      {(segmented.columns ?? [])
                        .filter((c: any) => c.id !== '__TOTAL__')
                        .map((c: any) => (
                          <td key={c.id} className="px-3 py-2 text-right">
                            {Number(r.bySegment?.[c.id] ?? 0).toLocaleString()}
                          </td>
                        ))}
                      <td className="px-3 py-2 text-right font-medium">
                        {Number(r.total).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'costCenters' && legalEntityId && (
        <div className="space-y-4">
          <form onSubmit={createCostCenter} className="grid gap-2 sm:grid-cols-4 items-end bg-white border rounded-lg p-4">
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Código</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={ccForm.code}
                onChange={(e) => setCcForm((f) => ({ ...f, code: e.target.value }))}
                required
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Nombre</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={ccForm.name}
                onChange={(e) => setCcForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Descripción</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={ccForm.description}
                onChange={(e) => setCcForm((f) => ({ ...f, description: e.target.value }))}
              />
            </label>
            <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm">
              Crear centro
            </button>
          </form>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Código</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Nombre</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Descripción</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Activo</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {costCenters.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-2 font-mono text-sm">{c.code}</td>
                    <td className="px-4 py-2 text-sm">{c.name}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{c.description ?? '—'}</td>
                    <td className="px-4 py-2 text-sm">{c.isActive ? 'Sí' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'businessUnits' && legalEntityId && (
        <div className="space-y-4">
          <form onSubmit={createBusinessUnit} className="grid gap-2 sm:grid-cols-4 items-end bg-white border rounded-lg p-4">
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Código</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={buForm.code}
                onChange={(e) => setBuForm((f) => ({ ...f, code: e.target.value }))}
                required
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Nombre</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={buForm.name}
                onChange={(e) => setBuForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Descripción</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={buForm.description}
                onChange={(e) => setBuForm((f) => ({ ...f, description: e.target.value }))}
              />
            </label>
            <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm">
              Crear unidad
            </button>
          </form>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Código</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Nombre</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Descripción</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Activo</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {businessUnits.map((b) => (
                  <tr key={b.id}>
                    <td className="px-4 py-2 font-mono text-sm">{b.code}</td>
                    <td className="px-4 py-2 text-sm">{b.name}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{b.description ?? '—'}</td>
                    <td className="px-4 py-2 text-sm">{b.isActive ? 'Sí' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'inflation' && legalEntityId && (
        <div className="space-y-6">
          <section className="bg-white border rounded-lg p-4 space-y-3">
            <h2 className="font-semibold text-gray-900">Índices</h2>
            <form onSubmit={createInflationSeries} className="grid gap-2 sm:grid-cols-4 items-end">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Código</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={seriesForm.code}
                  onChange={(e) => setSeriesForm((f) => ({ ...f, code: e.target.value }))}
                  required
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Nombre</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={seriesForm.name}
                  onChange={(e) => setSeriesForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Fuente</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={seriesForm.source}
                  onChange={(e) => setSeriesForm((f) => ({ ...f, source: e.target.value }))}
                />
              </label>
              <button
                type="submit"
                disabled={inflationBusy}
                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
              >
                Crear serie
              </button>
            </form>
            <div className="flex flex-wrap gap-3 items-end">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Serie activa</span>
                <select
                  className="border rounded-md px-2 py-1.5 text-sm"
                  value={selectedSeriesId}
                  onChange={async (e) => {
                    const id = e.target.value;
                    setSelectedSeriesId(id);
                    if (id) setInflationValues(await apiService.listInflationValues(id));
                    else setInflationValues([]);
                  }}
                >
                  <option value="">—</option>
                  {inflationSeries.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <form onSubmit={upsertInflationValue} className="grid gap-2 sm:grid-cols-3 items-end">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Período (YYYY-MM)</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={valueForm.period}
                  onChange={(e) => setValueForm((f) => ({ ...f, period: e.target.value }))}
                  placeholder="2026-01"
                  required
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Valor</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={valueForm.value}
                  onChange={(e) => setValueForm((f) => ({ ...f, value: e.target.value }))}
                  required
                />
              </label>
              <button
                type="submit"
                disabled={inflationBusy || !selectedSeriesId}
                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
              >
                Guardar valor
              </button>
            </form>
            <div className="overflow-hidden border rounded-md">
              <table className="min-w-full divide-y text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Período</th>
                    <th className="px-3 py-2 text-right text-xs uppercase text-gray-500">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {inflationValues.map((v) => (
                    <tr key={v.id}>
                      <td className="px-3 py-2 font-mono">{v.period}</td>
                      <td className="px-3 py-2 text-right">{Number(v.value).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white border rounded-lg p-4 space-y-3">
            <h2 className="font-semibold text-gray-900">Configuración</h2>
            {inflationPolicy && (
              <form onSubmit={saveInflationPolicy} className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!inflationPolicy.inflationAdjustmentEnabled}
                    onChange={(e) =>
                      setInflationPolicy((p: any) => ({
                        ...p,
                        inflationAdjustmentEnabled: e.target.checked,
                      }))
                    }
                  />
                  Ajuste habilitado (bloquea cierre si falta POSTED)
                </label>
                <label className="text-sm">
                  <span className="block text-xs text-gray-500">Motor</span>
                  <input
                    className="border rounded-md px-2 py-1.5 w-full"
                    value={inflationPolicy.inflationMathEngine || 'INDEX_RATIO'}
                    onChange={(e) =>
                      setInflationPolicy((p: any) => ({
                        ...p,
                        inflationMathEngine: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-xs text-gray-500">Serie de índice</span>
                  <select
                    className="border rounded-md px-2 py-1.5 w-full"
                    value={inflationPolicy.inflationIndexSeriesId || ''}
                    onChange={(e) =>
                      setInflationPolicy((p: any) => ({
                        ...p,
                        inflationIndexSeriesId: e.target.value || null,
                      }))
                    }
                  >
                    <option value="">—</option>
                    {inflationSeries.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.code}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-xs text-gray-500">Cuenta contrapartida</span>
                  <select
                    className="border rounded-md px-2 py-1.5 w-full"
                    value={inflationPolicy.inflationResultAccountId || ''}
                    onChange={(e) =>
                      setInflationPolicy((p: any) => ({
                        ...p,
                        inflationResultAccountId: e.target.value || null,
                      }))
                    }
                  >
                    <option value="">—</option>
                    {accounts
                      .filter((a) => a.isPosting)
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                  </select>
                </label>
                <button
                  type="submit"
                  disabled={inflationBusy}
                  className="sm:col-span-2 px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50 w-fit"
                >
                  Guardar policy
                </button>
              </form>
            )}
            <div className="overflow-auto border rounded-md max-h-64">
              <table className="min-w-full divide-y text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Cuenta</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Tipo monetario</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Ajustable</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {accounts
                    .filter((a) => a.isPosting)
                    .map((a) => (
                      <tr key={a.id}>
                        <td className="px-3 py-2">
                          <span className="font-mono text-gray-500">{a.code}</span> {a.name}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            className="border rounded px-1 py-0.5"
                            value={a.monetaryType || 'NOT_APPLICABLE'}
                            onChange={async (e) => {
                              try {
                                await apiService.patchAccountInflation(a.id, {
                                  legalEntityId,
                                  monetaryType: e.target.value,
                                });
                                await load();
                              } catch (err: any) {
                                setError(err?.response?.data?.message ?? err.message);
                              }
                            }}
                          >
                            <option value="NOT_APPLICABLE">N/A</option>
                            <option value="MONETARY">Monetaria</option>
                            <option value="NON_MONETARY">No monetaria</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={!!a.inflationAdjustable}
                            onChange={async (e) => {
                              try {
                                await apiService.patchAccountInflation(a.id, {
                                  legalEntityId,
                                  inflationAdjustable: e.target.checked,
                                });
                                await load();
                              } catch (err: any) {
                                setError(err?.response?.data?.message ?? err.message);
                              }
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white border rounded-lg p-4 space-y-3">
            <h2 className="font-semibold text-gray-900">Simulación</h2>
            <form onSubmit={runInflationSimulation} className="grid gap-2 sm:grid-cols-3 items-end">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Desde (YYYY-MM)</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={simForm.fromPeriod}
                  onChange={(e) => setSimForm((f) => ({ ...f, fromPeriod: e.target.value }))}
                  required
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Hasta (YYYY-MM)</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={simForm.toPeriod}
                  onChange={(e) => setSimForm((f) => ({ ...f, toPeriod: e.target.value }))}
                  required
                />
              </label>
              <button
                type="submit"
                disabled={inflationBusy}
                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
              >
                Calcular
              </button>
            </form>
          </section>

          {selectedRun && (
            <section className="bg-white border rounded-lg p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold text-gray-900">
                  Resultado · {selectedRun.status} · {selectedRun.fromPeriod}→{selectedRun.toPeriod}
                </h2>
                <div className="flex gap-2">
                  {selectedRun.status === 'CALCULATED' && (
                    <button
                      type="button"
                      disabled={inflationBusy}
                      onClick={postSelectedInflationRun}
                      className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-sm disabled:opacity-50"
                    >
                      Contabilizar
                    </button>
                  )}
                  {selectedRun.status === 'POSTED' && (
                    <button
                      type="button"
                      disabled={inflationBusy}
                      onClick={reverseSelectedInflationRun}
                      className="px-3 py-1.5 border border-red-300 text-red-700 rounded-md text-sm disabled:opacity-50"
                    >
                      Revertir
                    </button>
                  )}
                </div>
              </div>
              <div className="text-sm">
                AJUSTE TOTAL:{' '}
                <strong>
                  {Number(
                    (selectedRun.resultSnapshot as any)?.totals?.adjustmentSum ?? 0,
                  ).toLocaleString()}
                </strong>
                {(selectedRun.resultSnapshot as any)?.resultAccountCode && (
                  <span className="text-gray-500 ml-2">
                    Contrapartida {(selectedRun.resultSnapshot as any).resultAccountCode}
                  </span>
                )}
                {selectedRun.journalEntryId && (
                  <span className="text-gray-500 ml-2 font-mono text-xs">
                    JE {selectedRun.journalEntryId}
                  </span>
                )}
              </div>
              <div className="overflow-auto border rounded-md">
                <table className="min-w-full divide-y text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Cuenta</th>
                      <th className="px-3 py-2 text-right text-xs uppercase text-gray-500">Base</th>
                      <th className="px-3 py-2 text-right text-xs uppercase text-gray-500">Coef</th>
                      <th className="px-3 py-2 text-right text-xs uppercase text-gray-500">Ajuste</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {((selectedRun.resultSnapshot as any)?.lines ?? []).map((l: any) => (
                      <tr key={l.accountId}>
                        <td className="px-3 py-2">
                          <span className="font-mono text-gray-500">{l.code}</span> {l.name}
                        </td>
                        <td className="px-3 py-2 text-right">{Number(l.base).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{l.coefficient}</td>
                        <td className="px-3 py-2 text-right">
                          {Number(l.adjustment).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b font-semibold text-gray-900">Historial</div>
            <table className="min-w-full divide-y text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Períodos</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Estado</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Asiento</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {inflationRuns.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedRun(r)}
                  >
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.fromPeriod}→{r.toPeriod}
                    </td>
                    <td className="px-4 py-2">{r.status}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.journalEntryId ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}

      {tab === 'workingPapers' && legalEntityId && (
        <div className="space-y-4">
          <form
            className="grid gap-2 sm:grid-cols-3 items-end bg-white border rounded-lg p-4"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await apiService.createWorkingPaperPack({
                  legalEntityId,
                  fiscalYearId: wpForm.fiscalYearId,
                  title: wpForm.title,
                });
                setWpForm({ fiscalYearId: '', title: '' });
                await load();
              } catch (err: any) {
                setError(err?.response?.data?.message ?? err.message);
              }
            }}
          >
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Ejercicio</span>
              <select
                className="border rounded-md px-2 py-1.5 w-full"
                value={wpForm.fiscalYearId}
                onChange={(e) => setWpForm((f) => ({ ...f, fiscalYearId: e.target.value }))}
                required
              >
                <option value="">—</option>
                {fiscalYears.map((fy) => (
                  <option key={fy.id} value={fy.id}>
                    {fy.code} {fy.label ?? ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Título pack</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={wpForm.title}
                onChange={(e) => setWpForm((f) => ({ ...f, title: e.target.value }))}
                required
              />
            </label>
            <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm">
              Crear pack
            </button>
          </form>
          <form
            className="grid gap-2 sm:grid-cols-6 items-end bg-white border rounded-lg p-4"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const item = await apiService.addWorkingPaperItem(wpItemForm.packId, {
                  grouping: wpItemForm.grouping,
                  accountId: wpItemForm.accountId,
                  counterAccountId: wpItemForm.counterAccountId,
                  adjustmentDebit: wpItemForm.adjustmentDebit,
                  adjustmentCredit: 0,
                });
                await apiService.postWorkingPaperAdjustment(item.id);
                setWpItemForm({
                  packId: '',
                  grouping: '',
                  accountId: '',
                  counterAccountId: '',
                  adjustmentDebit: '',
                });
                await load();
              } catch (err: any) {
                setError(err?.response?.data?.message ?? err.message);
              }
            }}
          >
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Pack</span>
              <select
                className="border rounded-md px-2 py-1.5 w-full"
                value={wpItemForm.packId}
                onChange={(e) => setWpItemForm((f) => ({ ...f, packId: e.target.value }))}
                required
              >
                <option value="">—</option>
                {wpPacks.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} ({p.status})
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Agrupación</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={wpItemForm.grouping}
                onChange={(e) => setWpItemForm((f) => ({ ...f, grouping: e.target.value }))}
                required
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Cuenta</span>
              <select
                className="border rounded-md px-2 py-1.5 w-full"
                value={wpItemForm.accountId}
                onChange={(e) => setWpItemForm((f) => ({ ...f, accountId: e.target.value }))}
                required
              >
                <option value="">—</option>
                {accounts
                  .filter((a) => a.isPosting)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code}
                    </option>
                  ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Contrapartida</span>
              <select
                className="border rounded-md px-2 py-1.5 w-full"
                value={wpItemForm.counterAccountId}
                onChange={(e) => setWpItemForm((f) => ({ ...f, counterAccountId: e.target.value }))}
                required
              >
                <option value="">—</option>
                {accounts
                  .filter((a) => a.isPosting)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code}
                    </option>
                  ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500">Débito</span>
              <input
                className="border rounded-md px-2 py-1.5 w-full"
                value={wpItemForm.adjustmentDebit}
                onChange={(e) => setWpItemForm((f) => ({ ...f, adjustmentDebit: e.target.value }))}
                required
              />
            </label>
            <button type="submit" className="px-3 py-2 bg-emerald-600 text-white rounded-md text-sm">
              Agregar y contabilizar
            </button>
          </form>
          <div className="bg-white border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Pack</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Estado</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Ítems</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {wpPacks.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2">{p.title}</td>
                    <td className="px-4 py-2">{p.status}</td>
                    <td className="px-4 py-2">{(p.items ?? []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'imports' && legalEntityId && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <p className="text-sm text-gray-600">
              Flujo: subir → validar → vista previa → confirmar. Nada se escribe al mayor hasta
              confirmar.
            </p>
            <div className="grid gap-2 sm:grid-cols-4 items-end">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Tipo</span>
                <select
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={importType}
                  onChange={(e) => setImportType(e.target.value as typeof importType)}
                >
                  <option value="CHART_OF_ACCOUNTS">Plan de cuentas</option>
                  <option value="OPENING_BALANCES">Saldos iniciales</option>
                  <option value="JOURNAL_ENTRIES">Asientos</option>
                </select>
              </label>
              {importType === 'CHART_OF_ACCOUNTS' && (
                <label className="text-sm">
                  <span className="block text-xs text-gray-500">Modo chart</span>
                  <select
                    className="border rounded-md px-2 py-1.5 w-full"
                    value={importChartMode}
                    onChange={(e) => setImportChartMode(e.target.value as 'upsert' | 'createOnly')}
                  >
                    <option value="upsert">Upsert</option>
                    <option value="createOnly">Solo altas</option>
                  </select>
                </label>
              )}
              {importType === 'OPENING_BALANCES' && (
                <label className="text-sm">
                  <span className="block text-xs text-gray-500">Fecha asiento</span>
                  <input
                    type="date"
                    className="border rounded-md px-2 py-1.5 w-full"
                    value={importEntryDate}
                    onChange={(e) => setImportEntryDate(e.target.value)}
                  />
                </label>
              )}
              <label className="text-sm sm:col-span-2">
                <span className="block text-xs text-gray-500">Archivo CSV/XLSX</span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="block w-full text-sm"
                  onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!importFile || importBusy}
                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
                onClick={async () => {
                  if (!importFile) return;
                  setImportBusy(true);
                  setError(null);
                  try {
                    const job = await apiService.uploadAccountingImport({
                      legalEntityId,
                      type: importType,
                      file: importFile,
                      chartMode: importChartMode,
                      entryDate: importEntryDate || undefined,
                    });
                    setImportJob(job);
                    setImportJobs(await apiService.listAccountingImports(legalEntityId));
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err.message);
                  } finally {
                    setImportBusy(false);
                  }
                }}
              >
                1. Subir
              </button>
              <button
                type="button"
                disabled={!importJob || importBusy}
                className="px-3 py-2 border rounded-md text-sm disabled:opacity-50"
                onClick={async () => {
                  if (!importJob) return;
                  setImportBusy(true);
                  setError(null);
                  try {
                    const job = await apiService.validateAccountingImport(importJob.id);
                    setImportJob(job);
                    setImportJobs(await apiService.listAccountingImports(legalEntityId));
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err.message);
                  } finally {
                    setImportBusy(false);
                  }
                }}
              >
                2. Validar
              </button>
              <label className="flex items-center gap-2 text-sm px-2">
                <input
                  type="checkbox"
                  checked={importForce}
                  onChange={(e) => setImportForce(e.target.checked)}
                />
                Force (hash duplicado)
              </label>
              <button
                type="button"
                disabled={!importJob || importJob.status !== 'VALIDATED' || importBusy}
                className="px-3 py-2 bg-emerald-600 text-white rounded-md text-sm disabled:opacity-50"
                onClick={async () => {
                  if (!importJob) return;
                  setImportBusy(true);
                  setError(null);
                  try {
                    const job = await apiService.confirmAccountingImport(
                      importJob.id,
                      importForce,
                    );
                    setImportJob(job);
                    setImportJobs(await apiService.listAccountingImports(legalEntityId));
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err.message);
                  } finally {
                    setImportBusy(false);
                  }
                }}
              >
                3. Confirmar
              </button>
            </div>
            {importJob && (
              <div className="text-sm border-t pt-3 space-y-2">
                <div>
                  Job <span className="font-mono">{importJob.id}</span> ·{' '}
                  <span className="font-semibold">{importJob.status}</span>
                  {importJob.duplicateOfJobId ? (
                    <span className="text-amber-700"> · archivo duplicado</span>
                  ) : null}
                </div>
                {Array.isArray(importJob.errors) && importJob.errors.length > 0 && (
                  <ul className="text-red-700 text-xs space-y-1 max-h-40 overflow-auto">
                    {importJob.errors.map((e: any, i: number) => (
                      <li key={i}>
                        Fila {e.row}
                        {e.column ? ` / ${e.column}` : ''}: [{e.code}] {e.message}
                      </li>
                    ))}
                  </ul>
                )}
                {importJob.previewSnapshot && (
                  <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-48">
                    {JSON.stringify(importJob.previewSnapshot, null, 2)}
                  </pre>
                )}
                {importJob.resultSummary && (
                  <pre className="text-xs bg-emerald-50 p-2 rounded overflow-auto max-h-40">
                    {JSON.stringify(importJob.resultSummary, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
          <div className="bg-white border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Archivo</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Tipo</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Estado</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {importJobs.map((j) => (
                  <tr key={j.id}>
                    <td className="px-4 py-2">{j.fileName}</td>
                    <td className="px-4 py-2">{j.type}</td>
                    <td className="px-4 py-2">{j.status}</td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        className="text-blue-600 text-xs"
                        onClick={async () => {
                          setImportJob(await apiService.getAccountingImport(j.id));
                        }}
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'multicurrency' && legalEntityId && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={fxEnabled}
                onChange={async (e) => {
                  const v = e.target.checked;
                  setFxEnabled(v);
                  try {
                    await apiService.patchFxPolicy(legalEntityId, { fxRevaluationEnabled: v });
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err.message);
                  }
                }}
              />
              Revaluación FX habilitada (policy)
            </label>
            <p className="text-xs text-gray-500">
              Cotización = unidades de moneda base por 1 FC (ej. USD→ARS 1450). EEFF oficiales
              siguen en moneda base.
            </p>
            <form
              className="grid gap-2 sm:grid-cols-5 items-end"
              onSubmit={async (e) => {
                e.preventDefault();
                setFxBusy(true);
                setError(null);
                try {
                  await apiService.upsertExchangeRate({
                    legalEntityId,
                    currency: fxForm.currency,
                    date: fxForm.date,
                    rate: fxForm.rate,
                    source: fxForm.source,
                  });
                  setFxRates(await apiService.listExchangeRates(legalEntityId));
                } catch (err: any) {
                  setError(err?.response?.data?.message ?? err.message);
                } finally {
                  setFxBusy(false);
                }
              }}
            >
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Moneda</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={fxForm.currency}
                  onChange={(e) => setFxForm((f) => ({ ...f, currency: e.target.value }))}
                  required
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Fecha</span>
                <input
                  type="date"
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={fxForm.date}
                  onChange={(e) => setFxForm((f) => ({ ...f, date: e.target.value }))}
                  required
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Rate</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={fxForm.rate}
                  onChange={(e) => setFxForm((f) => ({ ...f, rate: e.target.value }))}
                  required
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500">Fuente</span>
                <input
                  className="border rounded-md px-2 py-1.5 w-full"
                  value={fxForm.source}
                  onChange={(e) => setFxForm((f) => ({ ...f, source: e.target.value }))}
                />
              </label>
              <button
                type="submit"
                disabled={fxBusy}
                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
              >
                Guardar cotización
              </button>
            </form>
            <table className="min-w-full divide-y text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Moneda</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Fecha</th>
                  <th className="px-3 py-2 text-right text-xs uppercase text-gray-500">Rate</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Fuente</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {fxRates.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2">{r.currency}</td>
                    <td className="px-3 py-2">{String(r.date).slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right font-mono">{String(r.rate)}</td>
                    <td className="px-3 py-2">{r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <label className="text-sm">
                <span className="block text-xs text-gray-500">As of</span>
                <input
                  type="date"
                  className="border rounded-md px-2 py-1.5"
                  value={fxAsOf}
                  onChange={(e) => setFxAsOf(e.target.value)}
                />
              </label>
              <button
                type="button"
                disabled={!fxAsOf || fxBusy}
                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
                onClick={async () => {
                  setFxBusy(true);
                  setError(null);
                  try {
                    const draft = await apiService.createFxRevaluation(legalEntityId, fxAsOf);
                    const calc = await apiService.calculateFxRevaluation(draft.id);
                    setFxRun(calc);
                    setFxRuns(await apiService.listFxRevaluations(legalEntityId));
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err.message);
                  } finally {
                    setFxBusy(false);
                  }
                }}
              >
                Calcular preview
              </button>
              <button
                type="button"
                disabled={!fxRun || fxRun.status !== 'CALCULATED' || fxBusy}
                className="px-3 py-2 bg-emerald-600 text-white rounded-md text-sm disabled:opacity-50"
                onClick={async () => {
                  setFxBusy(true);
                  setError(null);
                  try {
                    const posted = await apiService.postFxRevaluation(fxRun.id);
                    setFxRun(posted);
                    setFxRuns(await apiService.listFxRevaluations(legalEntityId));
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err.message);
                  } finally {
                    setFxBusy(false);
                  }
                }}
              >
                Postear
              </button>
              <button
                type="button"
                disabled={!fxRun || fxRun.status !== 'POSTED' || fxBusy}
                className="px-3 py-2 border rounded-md text-sm disabled:opacity-50"
                onClick={async () => {
                  setFxBusy(true);
                  setError(null);
                  try {
                    const rev = await apiService.reverseFxRevaluation(
                      fxRun.id,
                      'Reversión panel Multimoneda',
                    );
                    setFxRun(rev);
                    setFxRuns(await apiService.listFxRevaluations(legalEntityId));
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err.message);
                  } finally {
                    setFxBusy(false);
                  }
                }}
              >
                Revertir
              </button>
            </div>
            {fxRun && (
              <div className="text-sm space-y-2">
                <div>
                  Run <span className="font-mono">{fxRun.id}</span> ·{' '}
                  <span className="font-semibold">{fxRun.status}</span>
                </div>
                {fxRun.resultSnapshot?.lines && (
                  <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-48">
                    {JSON.stringify(fxRun.resultSnapshot.lines, null, 2)}
                  </pre>
                )}
              </div>
            )}
            <table className="min-w-full divide-y text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">As of</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Estado</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {fxRuns.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2">{String(r.asOfDate).slice(0, 10)}</td>
                    <td className="px-3 py-2">{r.status}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="text-blue-600 text-xs"
                        onClick={async () => setFxRun(await apiService.getFxRevaluation(r.id))}
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'balance' && legalEntityId && balanceSheet && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-4 py-3 border-b text-sm flex items-center justify-between gap-3">
            <div>
              Activo {Number(balanceSheet.equation?.totalAssets).toLocaleString()} · Pasivo+PN{' '}
              {Number(balanceSheet.equation?.liabilitiesPlusEquity).toLocaleString()} ·{' '}
              {balanceSheet.equation?.balanced ? 'Ecuación OK' : 'Descuadre'}
            </div>
            <button
              type="button"
              className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
              onClick={() =>
                apiService.downloadBalanceSheetPdf({
                  legalEntityId,
                  asOf: monthRange.asOf,
                })
              }
            >
              Descargar PDF
            </button>
          </div>
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Cuenta</th>
                <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Tipo</th>
                <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {balanceSheet.rows?.map((r: any) => (
                <tr key={r.code}>
                  <td className="px-4 py-2 text-sm">
                    <span className="font-mono text-gray-500">{r.code}</span> {r.name}
                  </td>
                  <td className="px-4 py-2 text-sm">{r.type}</td>
                  <td className="px-4 py-2 text-sm text-right">{Number(r.balance).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'cashflow' && legalEntityId && cashFlow && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-4 text-sm grid sm:grid-cols-4 gap-3">
            <div>
              <div className="text-xs text-gray-500">Ingresos</div>
              <div className="font-semibold">{Number(cashFlow.totals?.inflow).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Egresos</div>
              <div className="font-semibold">{Number(cashFlow.totals?.outflow).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Neto</div>
              <div className="font-semibold">{Number(cashFlow.totals?.netFlow).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Transferencias internas</div>
              <div className="font-semibold">
                {Number(cashFlow.totals?.internalTransfer).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Fecha</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Origen</th>
                  <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">Clase</th>
                  <th className="px-4 py-2 text-right text-xs uppercase text-gray-500">Monto</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {cashFlow.rows?.map((r: any) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2 text-sm">{new Date(r.date).toLocaleDateString('es-AR')}</td>
                    <td className="px-4 py-2 text-sm">{r.source}</td>
                    <td className="px-4 py-2 text-sm">{r.classification}</td>
                    <td className="px-4 py-2 text-sm text-right">{Number(r.amount).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'integrity' && legalEntityId && integrity && (
        <div className="space-y-4">
          <div
            className={
              'p-3 rounded-md text-sm border ' +
              (integrity.overall
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : 'bg-amber-50 border-amber-200 text-amber-900')
            }
          >
            Estado global: {integrity.overall ? 'OK' : 'Con hallazgos'} · {integrity.checkedAt}
          </div>
          {integrity.sections?.map((s: any) => (
            <div key={s.key} className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold text-sm mb-2">
                {s.key} · {s.ok ? 'OK' : 'Issues'}
              </h3>
              {(s.findings?.length ?? 0) === 0 ? (
                <p className="text-sm text-gray-500">Sin hallazgos</p>
              ) : (
                <ul className="text-sm space-y-1">
                  {s.findings.map((f: any, i: number) => (
                    <li key={i}>
                      <span className="font-mono text-xs">{f.severity}</span> {f.code}: {f.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
